import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaVideo, FaPlay } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const LiveSessions = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchSessions = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/live-sessions`, { headers: authHeaders() });
            const data = await res.json();
            setSessions(data.data || []);
        } catch (err) {
            toast.error("Could not load live sessions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSessions(); }, []);

    const toggleRegister = async (session) => {
        try {
            const method = session.registered ? "DELETE" : "POST";
            const res = await fetch(`${API_BASE_URL}/api/live-sessions/${session.id}/register`, { method, headers: authHeaders() });
            if (!res.ok) throw new Error("Failed to update registration");
            fetchSessions();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const now = new Date();
    const upcoming = sessions.filter((s) => new Date(s.scheduled_at) >= now);
    const past = sessions.filter((s) => new Date(s.scheduled_at) < now);

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate("/dashboard")} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white flex items-center gap-2"><FaVideo /> Live Sessions</h1>
            </nav>

            <div className="max-w-3xl mx-auto p-6 space-y-8">
                {loading && <p className="text-gray-500">Loading...</p>}

                {!loading && sessions.length === 0 && (
                    <p className="text-gray-500">No live sessions scheduled yet.</p>
                )}

                {upcoming.length > 0 && (
                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3">Upcoming</h3>
                        <div className="space-y-3">
                            {upcoming.map((s) => (
                                <div key={s.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                                    <div>
                                        <h4 className="font-semibold text-gray-800">{s.title}</h4>
                                        <p className="text-sm text-gray-500">{s.subject}{s.topic ? ` · ${s.topic}` : ""}</p>
                                        <p className="text-xs text-gray-400 mt-1">{new Date(s.scheduled_at).toLocaleString()}</p>
                                    </div>
                                    <div className="flex flex-col gap-2 items-end">
                                        <button onClick={() => toggleRegister(s)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${s.registered ? "bg-gray-100 text-gray-600" : "bg-blue600 text-white"}`}>
                                            {s.registered ? "Registered" : "Register"}
                                        </button>
                                        {s.registered && (
                                            <a href={s.external_link} target="_blank" rel="noreferrer" className="text-blue600 text-sm font-semibold flex items-center gap-1">
                                                <FaPlay size={10} /> Join
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {past.length > 0 && (
                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3">Recordings</h3>
                        <div className="space-y-3">
                            {past.filter((s) => s.recording_url).map((s) => (
                                <div key={s.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                                    <div>
                                        <h4 className="font-semibold text-gray-800">{s.title}</h4>
                                        <p className="text-sm text-gray-500">{s.subject}</p>
                                    </div>
                                    <a href={s.recording_url} target="_blank" rel="noreferrer" className="text-blue600 text-sm font-semibold">
                                        Watch
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveSessions;
